import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import type { ExcludeMatchType, ExcludedProduct } from '@sklad-sync/shared';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

export function AdminExcludedPage() {
  const [items, setItems] = useState<ExcludedProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ excluded: ExcludedProduct[] }>('/api/admin/excluded');
      setItems(data.excluded);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(id: number) {
    if (!confirm('Opravdu odstranit toto pravidlo? Při příštím syncu už produkt nebude vyřazený.'))
      return;
    try {
      await apiFetch(`/api/admin/excluded/${id}`, { method: 'DELETE' });
      setToast({ msg: 'Pravidlo smazáno', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Vyřazené produkty</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Pravidla, která se aplikují při reconciliaci. Produkty matchnuté pravidlem nebudou
            započítány do nesrovnalostí (např. Dýško, Twinsy, virtuální produkty).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
        >
          Přidat pravidlo
        </button>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg">
              <Th>Typ</Th>
              <Th>Hodnota</Th>
              <Th>Poznámka</Th>
              <Th>Vytvořeno</Th>
              <Th>Kdo</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody>
            {items == null ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-text-muted">
                  Zatím žádná pravidla. Klikni "Přidat pravidlo".
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-border-light hover:bg-surface-hover transition-colors"
                >
                  <td className="px-5 py-3">
                    <span
                      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                        it.match_type === 'code'
                          ? 'text-primary bg-primary-50'
                          : 'text-warning bg-warning-light'
                      }`}
                    >
                      {it.match_type === 'code' ? 'Hlavní kód' : 'Název obsahuje'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-text font-mono">{it.pattern}</td>
                  <td className="px-5 py-3 text-text-secondary">{it.note ?? '—'}</td>
                  <td className="px-5 py-3 text-text-secondary">{fmtDateTime(it.created_at)}</td>
                  <td className="px-5 py-3 text-text-secondary text-xs">{it.created_by ?? '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => remove(it.id)}
                      className="text-sm font-semibold text-danger hover:underline cursor-pointer"
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-text-muted max-w-2xl leading-relaxed">
        <strong>Jak to funguje:</strong> Pravidla se aplikují při dalším spuštění sync. Stávající
        snapshoty zůstávají beze změny (historie zachována). Pro retroaktivní aplikaci spusť sync
        znovu.
      </div>

      {adding && (
        <AddExcludedModal
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            setToast({ msg: 'Pravidlo přidáno', kind: 'success' });
            await load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider px-5 py-3">
      {children}
    </th>
  );
}

function AddExcludedModal({
  onClose,
  onSaved,
  onError,
}: {
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [matchType, setMatchType] = useState<ExcludeMatchType>('name');
  const [pattern, setPattern] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/admin/excluded', {
        method: 'POST',
        body: { match_type: matchType, pattern: pattern.trim(), note: note.trim() || undefined },
      });
      onSaved();
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-5">Nové pravidlo</h2>
        <hr className="border-border mb-5" />

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label
              className="text-sm font-medium text-text-secondary block mb-1.5"
              htmlFor="match-type"
            >
              Typ shody
            </label>
            <select
              id="match-type"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value as ExcludeMatchType)}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            >
              <option value="name">Název produktu obsahuje</option>
              <option value="code">Hlavní kód produktu</option>
            </select>
            <p className="text-xs text-text-muted mt-1.5">
              {matchType === 'name'
                ? 'Vyřadí všechny varianty produktů, jejichž název obsahuje tento text. Velká/malá písmena nevadí.'
                : 'Vyřadí všechny varianty produktu s tímto kódem (např. "K1055" vyřadí i K1055/110, K1055/122…).'}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5" htmlFor="pat">
              Hodnota
            </label>
            <input
              id="pat"
              type="text"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              required
              placeholder={matchType === 'name' ? 'Dýško' : '6533'}
              className="w-full text-sm text-text font-mono bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5" htmlFor="note">
              Poznámka (volitelná)
            </label>
            <input
              id="note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="proč je vyřazeno…"
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Přidat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
